/* ============================================================================
   VICO — Folhas caindo (motor Gemini endurecido). EXTERNALIZADO do snippet
   inline para assets/vico-leaves.js e carregado como script normal (defer),
   igual ao vico-flagship.js. Isso remove qualquer fragilidade de script inline
   (ordem de parsing / CSP / timing) que impedia o canvas de pintar no tema.

   Hardening preservado: startLoop() (nunca dois rAF), ctx.setTransform no resize,
   prefers-reduced-motion -> return (acessibilidade), IntersectionObserver, DPR,
   sprites pre-renderizados. Init robusto: canvas/ctx buscados em boot(), que roda
   no DOMContentLoaded se o DOM ainda estiver carregando.
   ========================================================================== */
(function () {
  'use strict';

  // =================================================================
  // 1. CONFIG
  // =================================================================
  const CONFIG = {
    COUNT_DESKTOP: 14,
    COUNT_MOBILE: 9,
    BIG_COUNT_DESKTOP: 2,
    BIG_COUNT_MOBILE: 1,

    SMALL_SIZE_RANGE: [17, 26],
    BIG_SCALE_MULT: 1.7,
    ASPECT_RATIO: 0.48,

    COLOR_CORE: '#2C5D45',
    COLOR_EDGE: '#0E2A1E',
    COLOR_VEIN: 'rgba(180, 220, 195, 0.22)',
    GLOSS_ALPHA: 0.15,
    BACKLIGHT_ALPHA: 0.08,

    FALL_SPEED_BASE: 0.65,
    FALL_SPEED_VAR: 0.35,
    FLUTTER_AMP_BASE: 0.7,
    FLUTTER_FREQ_BASE: 0.022,
    ROTATION_SPEED_BASE: 0.012,

    SCROLL_FORCE: 0.12,
    MAX_SCROLL_VEL: 12.0,
    TORQUE_LIMIT: 0.08,
    LERP_FACTOR: 0.06,
    IDLE_TIMEOUT: 150,

    EDGE_BIAS: 0.36,
    EDGE_PROB: 0.74,
    LAYER_BG_ALPHA: 0.42,
    LAYER_BG_BLUR: '2px'
  };

  // Acessibilidade: respeita movimento reduzido (o CSS tambem oculta o canvas).
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  let canvas = null;
  let ctx = null;
  let width = 0;
  let height = 0;
  let dpr = 1;
  let isMobile = false;
  let animationFrameId = null;
  let isVisible = true;

  let lastScrollY = window.scrollY;
  let scrollDelta = 0;
  let targetScrollImpact = 0;
  let currentScrollImpact = 0;
  let targetTorque = 0;
  let currentTorque = 0;
  let scrollTimeout = null;

  const spriteCache = [];
  let leaves = [];

  // =================================================================
  // 2. SPRITE ANATOMICO (offscreen)
  // =================================================================
  function makeLeafSprite(baseLength, variant) {
    const offCanvas = document.createElement('canvas');
    const offCtx = offCanvas.getContext('2d');

    const leafWidth = baseLength * CONFIG.ASPECT_RATIO;
    const padding = 12;
    const w = (leafWidth + padding) * 2;
    const h = (baseLength + padding) * 2;

    offCanvas.width = Math.ceil(w * dpr);
    offCanvas.height = Math.ceil(h * dpr);
    offCtx.scale(dpr, dpr);

    const cx = w / 2;
    const cy = h / 2;
    const halfL = baseLength / 2;
    const halfW = leafWidth / 2;

    offCtx.save();
    offCtx.translate(cx, cy);

    const curveOffset = (variant % 2 === 0 ? 1 : -1) * (baseLength * 0.05);

    offCtx.beginPath();
    const tipY = -halfL;
    const stemY = halfL;
    offCtx.moveTo(0, tipY);
    offCtx.bezierCurveTo(
      halfW * 1.15 + curveOffset, -halfL * 0.3,
      halfW * 1.05, halfL * 0.5,
      0, stemY
    );
    offCtx.bezierCurveTo(
      -halfW * 1.05, halfL * 0.5,
      -halfW * 1.15 + curveOffset, -halfL * 0.3,
      0, tipY
    );
    offCtx.closePath();

    const radGrad = offCtx.createRadialGradient(
      -halfW * 0.15, -halfL * 0.1, baseLength * 0.05,
      0, 0, halfL * 1.1
    );
    radGrad.addColorStop(0, CONFIG.COLOR_CORE);
    radGrad.addColorStop(0.7, '#163B2C');
    radGrad.addColorStop(1, CONFIG.COLOR_EDGE);

    offCtx.fillStyle = radGrad;
    offCtx.fill();

    offCtx.strokeStyle = 'rgba(160, 210, 185, ' + CONFIG.BACKLIGHT_ALPHA + ')';
    offCtx.lineWidth = 0.75;
    offCtx.stroke();

    offCtx.save();
    offCtx.clip();

    offCtx.beginPath();
    offCtx.moveTo(0, tipY + 2);
    offCtx.quadraticCurveTo(curveOffset * 0.3, 0, 0, stemY - 2);
    offCtx.strokeStyle = CONFIG.COLOR_VEIN;
    offCtx.lineWidth = 1.2;
    offCtx.stroke();

    const sideVeinPairs = 5;
    offCtx.lineWidth = 0.7;
    for (let i = 1; i <= sideVeinPairs; i++) {
      const t = i / (sideVeinPairs + 1);
      const yPos = tipY + (baseLength * t);
      const vWidth = halfW * Math.sin(t * Math.PI) * 0.85;

      offCtx.beginPath();
      offCtx.moveTo(0, yPos);
      offCtx.quadraticCurveTo(vWidth * 0.5, yPos - 6, vWidth, yPos - 12);
      offCtx.stroke();

      offCtx.beginPath();
      offCtx.moveTo(0, yPos);
      offCtx.quadraticCurveTo(-vWidth * 0.5, yPos - 6, -vWidth, yPos - 12);
      offCtx.stroke();
    }

    const glossGrad = offCtx.createLinearGradient(-halfW, -halfL * 0.5, halfW * 0.2, 0);
    glossGrad.addColorStop(0, 'rgba(255, 255, 255, ' + CONFIG.GLOSS_ALPHA + ')');
    glossGrad.addColorStop(0.5, 'rgba(255, 255, 255, ' + (CONFIG.GLOSS_ALPHA * 0.3) + ')');
    glossGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');

    offCtx.beginPath();
    offCtx.ellipse(-halfW * 0.3, -halfL * 0.25, halfW * 0.4, halfL * 0.35, -Math.PI / 6, 0, Math.PI * 2);
    offCtx.fillStyle = glossGrad;
    offCtx.fill();

    offCtx.restore();
    offCtx.restore();

    return { canvas: offCanvas, width: w, height: h };
  }

  function initSpriteCache() {
    spriteCache.length = 0;
    const sizes = [
      CONFIG.SMALL_SIZE_RANGE[0],
      CONFIG.SMALL_SIZE_RANGE[1],
      Math.round(CONFIG.SMALL_SIZE_RANGE[1] * CONFIG.BIG_SCALE_MULT)
    ];
    sizes.forEach(function (sz) {
      spriteCache.push(makeLeafSprite(sz, 0));
      spriteCache.push(makeLeafSprite(sz, 1));
    });
  }

  // =================================================================
  // 3. FACTORY DE PARTICULAS
  // =================================================================
  function createLeaf(isBig, isBg, initialSpawn) {
    const minSize = CONFIG.SMALL_SIZE_RANGE[0];
    const maxSize = CONFIG.SMALL_SIZE_RANGE[1];
    let baseSize = minSize + Math.random() * (maxSize - minSize);
    if (isBig) baseSize *= CONFIG.BIG_SCALE_MULT;

    const spriteIdx = isBig ? (Math.random() < 0.5 ? 4 : 5) : (Math.random() < 0.5 ? 0 : 2);

    let x;
    if (Math.random() < CONFIG.EDGE_PROB) {
      if (Math.random() < 0.5) {
        x = Math.random() * (width * CONFIG.EDGE_BIAS);
      } else {
        x = width - (Math.random() * (width * CONFIG.EDGE_BIAS));
      }
    } else {
      x = Math.random() * width;
    }

    const y = initialSpawn
      ? Math.random() * (height + 120) - 60
      : -80 - (Math.random() * 100);

    const sizeFactor = baseSize / maxSize;
    const speed = (CONFIG.FALL_SPEED_BASE + Math.random() * CONFIG.FALL_SPEED_VAR) * (0.85 + sizeFactor * 0.3);
    const flutterAmp = (CONFIG.FLUTTER_AMP_BASE + Math.random() * 0.5) / (0.8 + sizeFactor * 0.4);

    return {
      x: x, y: y, baseSize: baseSize, isBig: isBig, isBg: isBg,
      spriteIdx: Math.min(spriteIdx, spriteCache.length - 1),
      vy: speed, vx: 0,
      rx: Math.random() * Math.PI * 2,
      ry: Math.random() * Math.PI * 2,
      rz: Math.random() * Math.PI * 2,
      vrx: (CONFIG.ROTATION_SPEED_BASE + Math.random() * 0.015) * (Math.random() < 0.5 ? 1 : -1),
      vry: (CONFIG.ROTATION_SPEED_BASE + Math.random() * 0.012) * (Math.random() < 0.5 ? 1 : -1),
      vrz: (CONFIG.ROTATION_SPEED_BASE * 0.5 + Math.random() * 0.008) * (Math.random() < 0.5 ? 1 : -1),
      flutterPhase: Math.random() * Math.PI * 2,
      flutterFreq: CONFIG.FLUTTER_FREQ_BASE + Math.random() * 0.01,
      flutterAmp: flutterAmp
    };
  }

  function initLeaves() {
    leaves = [];
    const totalCount = isMobile ? CONFIG.COUNT_MOBILE : CONFIG.COUNT_DESKTOP;
    const bigCount = isMobile ? CONFIG.BIG_COUNT_MOBILE : CONFIG.BIG_COUNT_DESKTOP;
    const smallCount = totalCount - bigCount;
    const bgSmallCount = Math.floor(smallCount * 0.5);

    for (let i = 0; i < bgSmallCount; i++) leaves.push(createLeaf(false, true, true));
    for (let i = 0; i < (smallCount - bgSmallCount); i++) leaves.push(createLeaf(false, false, true));
    for (let i = 0; i < bigCount; i++) leaves.push(createLeaf(true, false, true));
  }

  // =================================================================
  // 4. RENDER + PROJECAO 3D
  // =================================================================
  function drawLeafInstance(leaf) {
    const spriteObj = spriteCache[leaf.spriteIdx];
    if (!spriteObj) return;

    ctx.save();
    ctx.translate(leaf.x, leaf.y);
    ctx.rotate(leaf.rz);

    const scaleX = Math.cos(leaf.ry);
    const scaleY = Math.cos(leaf.rx);
    const safeScaleX = Math.abs(scaleX) < 0.03 ? 0.03 * Math.sign(scaleX || 1) : scaleX;
    const safeScaleY = Math.abs(scaleY) < 0.03 ? 0.03 * Math.sign(scaleY || 1) : scaleY;
    ctx.scale(safeScaleX, safeScaleY);

    if (leaf.isBg) {
      ctx.globalAlpha = CONFIG.LAYER_BG_ALPHA;
    } else {
      const currentVy = leaf.vy + currentScrollImpact;
      if (currentVy > 3.5) {
        const blurStretch = 1 + Math.min((currentVy - 3.5) * 0.04, 0.35);
        ctx.scale(1, blurStretch);
        ctx.globalAlpha = 0.85;
      } else {
        ctx.globalAlpha = 1.0;
      }
    }

    ctx.drawImage(
      spriteObj.canvas,
      -spriteObj.width / 2,
      -spriteObj.height / 2,
      spriteObj.width,
      spriteObj.height
    );

    ctx.restore();
  }

  // =================================================================
  // 5. LOOP
  // =================================================================
  function startLoop() {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    animationFrameId = requestAnimationFrame(render);
  }

  function render() {
    if (!isVisible) return;

    currentScrollImpact += (targetScrollImpact - currentScrollImpact) * CONFIG.LERP_FACTOR;
    currentTorque += (targetTorque - currentTorque) * CONFIG.LERP_FACTOR;

    ctx.clearRect(0, 0, width, height);

    ctx.save();
    ctx.filter = 'blur(' + CONFIG.LAYER_BG_BLUR + ')';
    for (let i = 0; i < leaves.length; i++) {
      if (leaves[i].isBg) updateAndDrawLeaf(leaves[i]);
    }
    ctx.restore();

    for (let i = 0; i < leaves.length; i++) {
      if (!leaves[i].isBg) updateAndDrawLeaf(leaves[i]);
    }

    animationFrameId = requestAnimationFrame(render);
  }

  function updateAndDrawLeaf(leaf) {
    leaf.flutterPhase += leaf.flutterFreq;
    const flutterX = Math.sin(leaf.flutterPhase) * leaf.flutterAmp;

    const totalVy = leaf.vy + currentScrollImpact;
    leaf.y += totalVy;
    leaf.x += flutterX + (currentScrollImpact * 0.12 * Math.sin(leaf.rz));

    leaf.rx += leaf.vrx + currentTorque * 0.4;
    leaf.ry += leaf.vry + currentTorque * 0.6;
    leaf.rz += leaf.vrz + currentTorque * 0.2;

    if (leaf.y > height + 80 || leaf.x < -100 || leaf.x > width + 100) {
      const reset = createLeaf(leaf.isBig, leaf.isBg, false);
      Object.assign(leaf, reset);
    }

    drawLeafInstance(leaf);
  }

  // =================================================================
  // 6. EVENTOS
  // =================================================================
  function handleScroll() {
    const currentY = window.scrollY;
    scrollDelta = currentY - lastScrollY;
    lastScrollY = currentY;

    const rawImpact = Math.abs(scrollDelta) * CONFIG.SCROLL_FORCE;
    targetScrollImpact = Math.min(rawImpact, CONFIG.MAX_SCROLL_VEL);

    const rawTorque = scrollDelta * 0.003;
    targetTorque = Math.max(-CONFIG.TORQUE_LIMIT, Math.min(CONFIG.TORQUE_LIMIT, rawTorque));

    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(function () {
      targetScrollImpact = 0;
      targetTorque = 0;
    }, CONFIG.IDLE_TIMEOUT);
  }

  function handleResize() {
    dpr = window.devicePixelRatio || 1;
    width = window.innerWidth;
    height = window.innerHeight;
    isMobile = width < 768;

    canvas.width = Math.ceil(width * dpr);
    canvas.height = Math.ceil(height * dpr);
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    initSpriteCache();
    initLeaves();
  }

  const observer = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        if (!isVisible) { isVisible = true; startLoop(); }
      } else {
        isVisible = false;
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
      }
    });
  }, { threshold: 0 });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      isVisible = false;
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    } else {
      isVisible = true;
      startLoop();
    }
  });

  // =================================================================
  // 7. BOOT ROBUSTO
  // =================================================================
  function boot() {
    canvas = document.getElementById('vico-leaf-canvas');
    if (!canvas) return;
    ctx = canvas.getContext('2d');
    if (!ctx) return;

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    handleResize();
    observer.observe(canvas);
    startLoop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
